import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import VN from "../components/VN";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Select,
  TextField,
  Button,
  Container,
  Typography,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Autocomplete,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { apiRequest } from "../services/apiService";
import ReCAPTCHA from "react-google-recaptcha";
import { UserDataContext } from "../components/UserDataContext";

export default function RegisterSMS() {
  const { id } = useParams();
  const { userData } = useContext(UserDataContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [voivodeshipList, setVoivodeshipList] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [text] = useState("weryfikacja konta...");
  const isNanny = id === "1";

  const title = isNanny
    ? "REJESTRACJA OPIEKUNKI"
    : "REJESTRACJA KONTA OSOBY POSZUKUJĄCEJ OPIEKUNKĘ";

  // Walidacja dynamiczna
  const validationSchemaStep1 = Yup.object({
    login: Yup.string().required("Login jest wymagany"),

    email: Yup.string()
      .email("Podaj poprawny adres email")
      .test(
        "email-or-phone",
        "Podaj email lub numer telefonu",
        function (value) {
          const { phone } = this.parent;
          if (!value && !phone) return false;
          return true;
        },
      ),

    phone: Yup.string()
      .nullable()
      .test(
        "phone-or-email",
        "Podaj numer telefonu lub email",
        function (value) {
          const { email } = this.parent;
          if (!value && !email) return false;
          return true;
        },
      ),

    fullName: Yup.string().required("Nazwisko i imię są wymagane"),

    birthyear: isNanny
      ? Yup.number()
          .typeError("Podaj poprawny rok")
          .min(1960, "Podaj poprawny rok")
          .max(new Date().getFullYear() - 28, "Musisz mieć minimum 28 lat")
          .required("Rok urodzenia jest wymagany")
      : Yup.mixed().notRequired(),

    password: Yup.string()
      .min(6, "Hasło musi mieć min. 6 znaków")
      .required("Hasło jest wymagane"),

    passwordrepeat: Yup.string()
      .oneOf([Yup.ref("password"), null], "Hasła muszą być identyczne")
      .required("Powtórzenie hasła jest wymagane"),

    voivodeship: Yup.string().required("Wybierz województwo"),
    city: Yup.object().nullable().required("Wybierz miasto z listy"),

    rodo: Yup.boolean().oneOf(
      [true],
      "Musisz wyrazić zgodę na przetwarzanie danych",
    ),
  });

  const validationSchemaStep2 = Yup.object({
    code: Yup.string()
      .length(6, "Kod musi mieć 6 cyfr")
      .required("Kod jest wymagany"),
  });

  useEffect(() => {
    const fetchVoivodeships = async () => {
      try {
        const response = await apiRequest("voivodeship");
        setVoivodeshipList(response);
      } catch (error) {
        console.error("Błąd pobierania województw:", error.message);
      }
    };
    fetchVoivodeships();
  }, []);

  const fetchCitiesForVoivodeship = async (voivodeshipId) => {
    if (!voivodeshipId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const response = await apiRequest(`cities/${voivodeshipId}`);
      setCities(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Błąd miast:", error.message);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const initialValues = {
    login: "",
    email: "",
    fullName: "",
    phone: "",
    birthyear: "",
    password: "",
    passwordrepeat: "",
    voivodeship: "",
    city: null,
    rodo: false,
  };

  return (
    <div>
      <VN userData={userData} />
      <Container maxWidth="sm">
        <Box mt={5} mb={5}>
          <Typography variant="h6" color="primary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
            Zarejestruj swoje konto
          </Typography>

          {step === 1 && (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchemaStep1}
              onSubmit={async (values, { setSubmitting }) => {
                if (isNanny && !captchaToken) {
                  alert("Proszę potwierdzić, że nie jesteś robotem.");
                  setSubmitting(false);
                  return;
                }

                try {
                  const { birthyear, ...otherValues } = values;
                  const finalValues = isNanny ? values : otherValues;

                  const payload = {
                    ...finalValues,
                    city: values.city.id,
                    captcha: isNanny ? captchaToken : undefined,
                    accountType: id,
                  };

                  const response = await apiRequest(
                    "register",
                    "POST",
                    payload,
                  );
                  console.log("response:", response);
                  if (isNanny) {
                    setPhone(values.phone);
                    setStep(2);
                  } else {
                    // Przekierowanie do logowania ze statusem sukcesu (bez alertu)
                    navigate("/login", { state: { registered: true } });
                  }
                } catch (e) {
                  alert("Błąd rejestracji. Spróbuj ponownie.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                handleChange,
                setFieldValue,
                touched,
                errors,
                isSubmitting,
              }) => {
                const currentYear = new Date().getFullYear();
                const birthYears = [];
                for (let y = currentYear - 28; y >= 1960; y--) {
                  birthYears.push(y);
                }

                return (
                  <Form>
                    <TextField
                      fullWidth
                      label="Konto"
                      name="login"
                      margin="normal"
                      value={values.login}
                      onChange={handleChange}
                      error={touched.login && Boolean(errors.login)}
                      helperText={touched.login && errors.login}
                    />

                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      margin="normal"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                    {text}
                    <TextField
                      fullWidth
                      label="Nazwisko i Imię"
                      name="fullName"
                      margin="normal"
                      value={values.fullName}
                      onChange={handleChange}
                      error={touched.fullName && Boolean(errors.fullName)}
                      helperText={touched.fullName && errors.fullName}
                    />
                    <TextField
                      fullWidth
                      label="Telefon"
                      name="phone"
                      margin="normal"
                      value={values.phone}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 9);
                        setFieldValue("phone", value);
                      }}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+48</InputAdornment>
                        ),
                      }}
                    />

                    {isNanny && (
                      <FormControl
                        fullWidth
                        margin="normal"
                        error={touched.birthyear && Boolean(errors.birthyear)}
                      >
                        <InputLabel>Rok urodzenia</InputLabel>
                        <Select
                          name="birthyear"
                          value={values.birthyear}
                          label="Rok urodzenia"
                          onChange={handleChange}
                        >
                          {birthYears.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.birthyear && (
                          <FormHelperText>{errors.birthyear}</FormHelperText>
                        )}
                      </FormControl>
                    )}

                    <TextField
                      fullWidth
                      label="Hasło"
                      type="password"
                      name="password"
                      margin="normal"
                      value={values.password}
                      onChange={handleChange}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />

                    <TextField
                      fullWidth
                      label="Powtórz hasło"
                      type="password"
                      name="passwordrepeat"
                      margin="normal"
                      value={values.passwordrepeat}
                      onChange={handleChange}
                      error={
                        touched.passwordrepeat && Boolean(errors.passwordrepeat)
                      }
                      helperText={
                        touched.passwordrepeat && errors.passwordrepeat
                      }
                    />

                    <FormControl
                      fullWidth
                      margin="normal"
                      error={touched.voivodeship && Boolean(errors.voivodeship)}
                    >
                      <InputLabel>Województwo</InputLabel>
                      <Select
                        name="voivodeship"
                        value={values.voivodeship}
                        label="Województwo"
                        onChange={(e) => {
                          const vId = e.target.value;
                          setFieldValue("voivodeship", vId);
                          setFieldValue("city", null);
                          fetchCitiesForVoivodeship(vId);
                        }}
                      >
                        {voivodeshipList.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.nazwa}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.voivodeship && (
                        <FormHelperText>{errors.voivodeship}</FormHelperText>
                      )}
                    </FormControl>

                    <Autocomplete
                      options={cities}
                      getOptionLabel={(option) => option.nazwa || ""}
                      value={values.city}
                      disabled={!values.voivodeship}
                      loading={loadingCities}
                      onChange={(_, newValue) =>
                        setFieldValue("city", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            isNanny
                              ? "Miasto zamieszkania"
                              : "Miasto, w którym szukasz opiekunki"
                          }
                          margin="normal"
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCities ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="rodo"
                          checked={values.rodo}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Wyrażam zgodę na przetwarzanie danych...{" "}
                          <a href="/polityka-prywatnosci" target="_blank">
                            Polityka prywatności
                          </a>
                          .
                        </Typography>
                      }
                    />
                    {touched.rodo && errors.rodo && (
                      <FormHelperText error>{errors.rodo}</FormHelperText>
                    )}

                    <Box mt={2} mb={2} display="flex" justifyContent="center">
                      <ReCAPTCHA
                        sitekey="6Ld8dZcsAAAAAI2kdXLZVwduM4ydXysrZRJju4qo"
                        onChange={(token) => setCaptchaToken(token)}
                      />
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={isSubmitting}
                      sx={{ mt: 2, height: "56px" }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : isNanny ? (
                        "Zarejestruj i wyślij SMS"
                      ) : (
                        "Zarejestruj"
                      )}
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          )}

          {step === 2 && (
            <Formik
              initialValues={{ code: "" }}
              validationSchema={validationSchemaStep2}
              onSubmit={async (values) => {
                try {
                  await apiRequest("verify-sms", "POST", {
                    phone,
                    code: values.code,
                  });
                  navigate("/login", { state: { registered: true } });
                } catch (e) {
                  alert("Nieprawidłowy kod weryfikacyjny");
                }
              }}
            >
              {({ values, handleChange, errors, touched, isSubmitting }) => (
                <Form>
                  <Box textAlign="center" mb={3}>
                    <Typography variant="h6">Weryfikacja SMS</Typography>
                    <Typography color="textSecondary">
                      Wpisz 6-cyfrowy kod wysłany na numer: <b>+48 {phone}</b>
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    label="Kod SMS"
                    name="code"
                    margin="normal"
                    value={values.code}
                    onChange={handleChange}
                    error={touched.code && Boolean(errors.code)}
                    helperText={touched.code && errors.code}
                    inputProps={{
                      maxLength: 6,
                      style: {
                        textAlign: "center",
                        letterSpacing: "5px",
                        fontSize: "1.5rem",
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    disabled={isSubmitting}
                    sx={{ mt: 3, height: "56px" }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Potwierdź aktywację"
                    )}
                  </Button>
                  <Button
                    fullWidth
                    sx={{ mt: 1 }}
                    onClick={() => setStep(1)}
                    disabled={isSubmitting}
                  >
                    Wróć do edycji danych
                  </Button>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Container>
    </div>
  );
}
